import { useInkind } from '@gik/api/inkinds/inkind';
import type { RelationFilter } from '@gik/api/posts/PostsUrlBuilder';
import { usePostsRelated } from '@gik/api/posts/postsRelated';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import {
  NetworkError,
  NetworkRetryMessage,
  SkeletonNetworkError,
  SkeletonNetworkRetryMessage,
} from '@gik/core/components/NetworkError/NetworkError';
import bemBlock from '@gik/core/utils/bemBlock';
import { ArticleTile } from '@gik/ui-legacy/gik/ArticleTile';
import { FrameBone, ParagraphBone } from '@gik/ui-legacy/SkeletonLoader';
import { useStore } from '@nanostores/react';
import classnames from 'classnames';
import type React from 'react';
import * as NetworkStoreApi from '@gik/core/stores/NetworkStore.ts';
import { default as NetworkStore } from '@gik/core/stores/NetworkStore.ts';

interface IRelatedArticlesProps extends React.HTMLAttributes<HTMLDivElement> {
  skeletonItems?: number;
  relationFilters?: RelationFilter<string[]>[];
  inkindRouteId: string;

  showRetrying?: string | JSX.Element;
  showError?: string | JSX.Element;
  onReload?: () => void;
}

const skelBem = bemBlock(`related-articles-rows-skeleton`);
const Skeleton = ({
  skeletonItems = relatedArticlesNumItems,
  onReload,
  showRetrying,
  showError,
}: Partial<IRelatedArticlesProps>): React.ReactElement => (
  <div className="relative">
    <div className={`${skelBem()}`}>
      {Array.from(Array(skeletonItems).keys()).map(index => {
        return (
          <div key={index} className={skelBem('item')}>
            <FrameBone className={skelBem('image')} />
            <main>
              <ParagraphBone words={20} className={skelBem('text')} />
            </main>
          </div>
        );
      })}
    </div>
    {showRetrying && !showError && <SkeletonNetworkRetryMessage />}
    {showError && <SkeletonNetworkError onReload={onReload} />}
  </div>
);

export function getRelatedArticlesForInkindPageFilters(situationSlugs: string[]): RelationFilter<string[]>[] {
  return [
    {
      taxonomy: 'situation',
      terms: situationSlugs,
    },
    {
      taxonomy: 'article-type',
      terms: ['being-supportive'],
    },
  ];
}
export const relatedArticlesNumItems = 3;

export function RelatedArticles({
  relationFilters,
  inkindRouteId,
  className,
}: IRelatedArticlesProps): React.ReactElement {
  const requestKey = `inkind-${inkindRouteId}-related-articles`;

  // either inkindRouteId or relationFilters must be defined
  const { data: inkindPage, error: inkindPageError } = useInkind(inkindRouteId);

  const $networkStore = useStore(NetworkStore);

  const situationSlugs = inkindRouteId && inkindPage?.inkindSituations?.map(s => s.slug);

  // FIXME: we should always pass relationFilters in
  // rather than doing this weird check - RelatedArticles should not rely on an inkind page.
  const _relationFilters: RelationFilter<string[]>[] = inkindRouteId
    ? getRelatedArticlesForInkindPageFilters(situationSlugs)
    : relationFilters;

  const {
    data: relatedPostsData,
    error: relatedPostsDataError,
    mutate,
  } = usePostsRelated(
    {
      relationFilters: _relationFilters,
      perPage: relatedArticlesNumItems,
    },
    {
      requestKey,
    }
  );

  const bem = bemBlock('related-articles-rows');
  const blockClasses = classnames(bem(), className || '');

  const isLoading = !inkindPage || !relatedPostsData;

  if (isLoading || !relatedPostsData || !NetworkStoreApi.isSuccess(requestKey))
    return (
      <Skeleton
        showRetrying={NetworkStoreApi.shouldShowRetryMessage(requestKey)}
        showError={NetworkStoreApi.isError(requestKey)}
        onReload={() => {
          NetworkStoreApi.reload(requestKey);
          mutate();
        }}
      />
    );

  const articles = relatedPostsData
    ? relatedPostsData
        ?.map(post => {
          return WordpressConverter.convertPost(post);
        })
        .filter(WordpressConverter.filterOutPreviewContent)
    : null;

  return (
    <div className={blockClasses}>
      {false && $networkStore}
      {articles?.map(item => {
        if (!item) return null;
        return <ArticleTile article={item} key={item.articleId} layout={'row'} />;
      })}
    </div>
  );
}
